import { useState, useEffect, useContext } from "react";
import { X, ScrollText, Shield, Cookie, FileText } from "lucide-react";
import { AuthContext } from "@/App";
import { legalTexts } from "@/data/legalTexts";
import ReactMarkdown from "react-markdown";

const SECTION_META = {
  terms:   { icon: ScrollText, dataKey: "termsAndConditions" },
  privacy: { icon: Shield,     dataKey: "privacyPolicy" },
  cookies: { icon: Cookie,     dataKey: "cookiesPolicy" },
  legal:   { icon: FileText,   dataKey: "legalNotice" },
};

const LegalModal = ({ isOpen, onClose, section = "terms", defaultLanguage }) => {
  const authContext = useContext(AuthContext);
  const [language, setLanguage] = useState(defaultLanguage || "es");

  useEffect(() => {
    if (defaultLanguage) {
      setLanguage(defaultLanguage);
    } else if (authContext?.language) {
      setLanguage(authContext.language);
    }
  }, [authContext?.language, defaultLanguage]);

  if (!isOpen) return null;

  const lang = language === "ca" ? "ca" : "es";
  const meta = SECTION_META[section] || SECTION_META.terms;
  const Icon = meta.icon;
  const data = legalTexts[meta.dataKey][lang];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-slate-900 border border-teal-400/20 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700/50 shrink-0">
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-teal-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">{data.title}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{data.lastUpdated}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              <button
                data-testid="legal-lang-ca"
                onClick={() => setLanguage("ca")}
                className={`px-2 py-1 text-xs rounded transition-colors ${lang === "ca" ? "bg-teal-500/20 text-teal-300 border border-teal-500/30" : "text-gray-400 hover:text-gray-300"}`}
              >
                CA
              </button>
              <button
                data-testid="legal-lang-es"
                onClick={() => setLanguage("es")}
                className={`px-2 py-1 text-xs rounded transition-colors ${lang === "es" ? "bg-teal-500/20 text-teal-300 border border-teal-500/30" : "text-gray-400 hover:text-gray-300"}`}
              >
                ES
              </button>
            </div>
            <button
              data-testid="legal-modal-close"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-700/50 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="prose prose-invert prose-sm max-w-none
            prose-headings:text-teal-300 prose-headings:font-semibold
            prose-h2:text-base prose-h2:mt-6 prose-h2:mb-3
            prose-h3:text-sm prose-h3:mt-4 prose-h3:mb-2
            prose-p:text-gray-300 prose-p:leading-relaxed prose-p:text-sm
            prose-li:text-gray-300 prose-li:text-sm
            prose-strong:text-white
            prose-a:text-teal-300 prose-a:no-underline hover:prose-a:underline
            prose-ul:my-2 prose-ol:my-2
          ">
            <ReactMarkdown>{data.content}</ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700/50 shrink-0">
          <button
            data-testid="legal-modal-accept"
            onClick={onClose}
            className="w-full btn-primary py-2.5 text-sm"
          >
            {lang === "ca" ? "Tancar" : "Cerrar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
